import React, { useState, useEffect } from "react";
import "./ProfitLoss.css";

const ProfitLoss = () => {
  const [superAdminId, setSuperAdminId] = useState("");
  const [branches, setBranches] = useState([]);
  const [filters, setFilters] = useState({
    branchId: "",
    fromDate: "",
    toDate: "",
    voucherType: "",
    createdBy: "",
    employeeId: "",
    memberId: "",
  });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const fetchBranches = async () => {
    if (!superAdminId) return;
    try {
      const res = await fetch(
        `https://payyug.in/api/get-branch?SuperAdminId=${encodeURIComponent(superAdminId)}&limit=all`
      );
      if (!res.ok) throw new Error(`Branch fetch HTTP ${res.status}`);
      const json = await res.json();
      setBranches(json.branches || []);
    } catch (err) {
      console.error("Branch fetch error", err);
    }
  };

  useEffect(() => {
    const adminId = localStorage.getItem("adminuserId") || localStorage.getItem("superAdminId") || "";
    setSuperAdminId(adminId);
  }, []);

  useEffect(() => {
    fetchBranches();
  }, [superAdminId]);

  const fetchProfitLoss = async () => {
    if (!superAdminId) {
      setError("Unable to locate superAdminId in localStorage.");
      return;
    }
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const params = new URLSearchParams({ superAdminId });
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      const url = `https://payyug.in/api/report/profit-loss?${params.toString()}`;
      const res = await fetch(url);
      if (!res.ok) {
        const errorJson = await res.json().catch(() => null);
        const message = errorJson?.message || errorJson?.error || `HTTP ${res.status}`;
        throw new Error(message);
      }
      const json = await res.json();
      setData(json);
    } catch (err) {
      setError(err.message || "Fetch error");
    } finally {
      setLoading(false);
    }
  };

  const renderList = (list) => {
    if (!Array.isArray(list) || list.length === 0) return <div>No items</div>;
    return (
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Head</th>
            <th>Opening Balance</th>
            <th>DR</th>
            <th>CR</th>
          </tr>
        </thead>
        <tbody>
          {list.map((item, idx) => (
            <tr key={idx}>
              <td>{idx + 1}</td>
              <td>{item.head || item.name || item.title || "-"}</td>
              <td>{item.openingBalance ?? item.opening_balance ?? "-"}</td>
              <td>{item.dr ?? item.debit ?? "-"}</td>
              <td>{item.cr ?? item.credit ?? "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className="profitLoss-container">
      <h1 className="profitLoss-title">Profit - Loss</h1>

      <div className="profitLoss-filterBox">
        <div className="profitLoss-filter-title">Filter By</div>
        <div className="profitloss-filter-item-div">
          <div className="profitloss-filter-item">
            <label>Branch</label>
            <select name="branchId" value={filters.branchId} onChange={handleChange}>
              <option value="">All branches</option>
              {branches.map((branch) => {
                const value = branch.branchId ?? branch._id ?? branch.id ?? "";
                const label = branch.branchName || branch.name || branch.branch || "Branch";
                return (
                  <option key={value || label} value={value}>
                    {label}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="profitloss-filter-item">
            <label>From</label>
            <input name="fromDate" type="date" value={filters.fromDate} onChange={handleChange} />
          </div>

          <div className="profitloss-filter-item">
            <label>To</label>
            <input name="toDate" type="date" value={filters.toDate} onChange={handleChange} />
          </div>

          <div className="profitloss-filter-item">
            <label>Voucher Type</label>
            <input name="voucherType" value={filters.voucherType} onChange={handleChange} />
          </div>

          <div className="profitloss-filter-item">
            <label>Created By</label>
            <input name="createdBy" value={filters.createdBy} onChange={handleChange} />
          </div>

          <div className="profitloss-filter-item">
            <label>Employee ID</label>
            <input name="employeeId" value={filters.employeeId} onChange={handleChange} />
          </div>

          <div className="profitloss-filter-item">
            <label>Member ID</label>
            <input name="memberId" value={filters.memberId} onChange={handleChange} />
          </div>

          <button
            className="profitloss-filter-btn"
            onClick={fetchProfitLoss}
            disabled={loading || !superAdminId}
          >
            {loading ? "Loading..." : "Filter"}
          </button>
        </div>
      </div>

      <div className="profitLoss-report-container">
        <h2 className="profitLoss-section-title">Profit Loss</h2>
        <div className="profitLoss-report-section">
          {error && <div style={{ color: "red" }}>Error: {error}</div>}

          <div className="profitLoss-income-section">
            <h2>Income</h2>
            <p>Indirect Income: {data?.income ?? data?.indirectIncome ?? data?.indirect_income ?? "-"}</p>
            <div className="profitLoss-income-box">
              {Array.isArray(data?.income) || Array.isArray(data?.incomes) || Array.isArray(data?.income_list)
                ? renderList(data?.income ?? data?.incomes ?? data?.income_list ?? [])
                : null}
            </div>
          </div>

          <div className="profitLoss-expense-section">
            <h2>Expense</h2>
            <p>Indirect Expense: {data?.expense ?? data?.indirectExpense ?? data?.indirect_expense ?? "-"}</p>
            <div className="profitLoss-expense-box">
              {Array.isArray(data?.expense) || Array.isArray(data?.expenses) || Array.isArray(data?.expense_list)
                ? renderList(data?.expense ?? data?.expenses ?? data?.expense_list ?? [])
                : null}
            </div>
          </div>

          {data?.profit != null && (
            <div className="profitLoss-profit-section">
              <h2>Profit</h2>
              <p>{data.profit}</p>
            </div>
          )}

          {/* If API returns unexpected shape, show JSON for debugging */}
          {data && !data.income && !data.expense && (
            <div style={{ marginTop: 12 }}>
              <h3>Raw Response</h3>
              <pre style={{ maxHeight: 300, overflow: "auto" }}>{JSON.stringify(data, null, 2)}</pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfitLoss;